/**
 * NSW RLGSP legend helpers — wired into `nswRlgspConfig.parseLegendLayers`.
 *
 * Generic walk of the `layerConfig.ts` table: every layer with a built source
 * becomes one `MapLayer` carrying a single checkable legend item (the whole
 * layer on/off), which is the shape `LayerList` renders.
 *
 * `syncVisibleLayers` is the only `setFilter` writer (no MIRE-style explorer
 * overlay). Every layer opts out via `excludeFromMapDataFilter`, otherwise the
 * global filter would replace style filters such as the crash bands.
 */

import { moduleTable, type INswRlgspSystemLayers } from './layerConfig';
import type { ILayerSource } from '#lib/types.js';
import type { MapLayer } from '#lib/stores/legendStore.svelte.js';

// Representative patch colour for a layer: its `<type>-color` paint property
// when that is a plain colour string. Expression-driven paints (match/step
// bands) have no single colour — those rows fall back to the patch default
// in `LayerList`.
function paintColor(source: ILayerSource): string | undefined {
	const paint = (source.layer.paint ?? {}) as Record<string, unknown>;
	const color = paint[`${source.layer.type}-color`];
	return typeof color === 'string' ? color : undefined;
}

export function parseLegendLayers(payload: INswRlgspSystemLayers): MapLayer[] {
	const layers = payload as Record<string, Record<string, ILayerSource | null>>;
	const mapLayers: MapLayer[] = [];

	for (const [moduleId, m] of Object.entries(moduleTable)) {
		for (const [layerId, entry] of Object.entries(m.layers)) {
			const source = layers?.[moduleId]?.[layerId];
			if (!source?.layer?.id) continue;
			// `partOf` sublayers have no own legend row — they start (and stay)
			// in step with their primary layer's visibility.
			const enabled =
				entry.legend?.initiallyVisible ??
				(entry.legend?.partOf
					? m.layers[entry.legend.partOf]?.legend?.initiallyVisible
					: undefined) ??
				false;
			mapLayers.push({
				systemId: source.id,
				layerId: source.layer.id,
				initialFilter: source.layer.filter,
				minzoom: source.layer.minzoom,
				filterAttr: '',
				legendItems: [
					{
						legendItemId: `${source.id}:${source.layer.type}`,
						label: layerId,
						color: paintColor(source),
						enabled
					}
				],
				layerAllEnabled: enabled,
				layerAnyEnabled: enabled,
				excludeFromMapDataFilter: true
			});
		}
	}

	return mapLayers;
}
